import React, { useMemo, useState } from 'react'
import { Calendar, Modal } from 'antd'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { DutyShift } from '../../lib/api'

/**
 * 值班日历视图（dutyshift_record_calendar_v1）：
 * antd Calendar 月历形态，每个日期格列出当天值班人用户名；
 * 点击某一天 → 父组件接管编辑（单条直接改，多条先选哪条，无班次弹新增）。
 * 年月选择走 Calendar 自带 onPanelChange（受控 value），切换即触发父组件按该月重拉。
 */
type Props = {
  list: DutyShift[]
  operators: Array<{ id: number; username: string }>
  canEdit: boolean
  /** 受控年月（任一落在该月的日期），父组件持有；undefined 时用今天 */
  panelMonth: Dayjs
  onPanelMonthChange: (d: Dayjs) => void
  /** 点单日回调：shift 为 null 表示该日无班次（父组件可弹新增预填 day） */
  onPickDay: (day: Dayjs, shifts: DutyShift[]) => void
}

const DutyShiftCalendar: React.FC<Props> = ({ list, operators, canEdit, panelMonth, onPanelMonthChange, onPickDay }) => {
  const [pickDayList, setPickDayList] = useState<{ day: Dayjs; shifts: DutyShift[] } | null>(null)

  const usernameOf = (adminUserId: number): string => {
    const u = operators.find((o) => o.id === adminUserId)
    return u ? u.username : `#${adminUserId}`
  }

  /** 同一天（本地日）内的全部班次 */
  const shiftsOfDay = useMemo(() => {
    const map = new Map<string, DutyShift[]>()
    for (const s of list) {
      const key = dayjs(s.startAt).format('YYYY-MM-DD')
      const arr = map.get(key) ?? []
      arr.push(s)
      map.set(key, arr)
    }
    return map
  }, [list])

  const fullCellRender = (date: Dayjs) => {
    const key = date.format('YYYY-MM-DD')
    const shifts = shiftsOfDay.get(key) ?? []
    const isToday = date.isSame(dayjs(), 'day')
    // 跨月格（当前面板年月之外的日期）淡化显示
    const inMonth = date.month() === panelMonth.month()
    return (
      <div
        style={{
          minHeight: 96,
          padding: '4px 6px',
          border: isToday ? '2px solid #1A873B' : '1px solid #f0f0f0',
          borderRadius: 8,
          background: inMonth ? (isToday ? '#f6ffed' : '#fff') : '#fafafa',
          overflow: 'hidden',
          opacity: inMonth ? 1 : 0.55,
        }}
      >
        <div
          style={{
            fontWeight: isToday ? 700 : 600,
            color: isToday ? '#1A873B' : inMonth ? '#333' : '#bbb',
            fontSize: 13,
            lineHeight: '18px',
          }}
        >
          {date.date()}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 3 }}>
          {shifts.map((s) => (
            <div
              key={s.id}
              title={usernameOf(s.adminUserId)}
              style={{
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
                background: inMonth ? '#e6f4ea' : '#f0f0f0',
                color: inMonth ? '#1A873B' : '#999',
                fontSize: 11,
                lineHeight: '16px',
                padding: '0 6px',
                borderRadius: 6,
              }}
            >
              {usernameOf(s.adminUserId)}
            </div>
          ))}
        </div>
      </div>
    )
  }

  const handleSelect = (date: Dayjs, info: { source: 'date' | 'month' | 'year' | 'customize' }) => {
    // 仅响应点某天（date）；头部月/年切换由 onPanelChange 处理
    if (info.source !== 'date') return
    // 日期格本身渲染了日期数字（fullCellRender 覆盖默认格），这里直接取该日班次
    const key = date.format('YYYY-MM-DD')
    const shifts = shiftsOfDay.get(key) ?? []
    if (shifts.length === 1) {
      onPickDay(date, shifts)
    } else if (shifts.length > 1) {
      setPickDayList({ day: date, shifts })
    } else if (canEdit) {
      onPickDay(date, [])
    }
  }

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 8 }}>
      <Calendar
        fullscreen
        value={panelMonth}
        onPanelChange={(d) => onPanelMonthChange(d)}
        onSelect={handleSelect}
        fullCellRender={fullCellRender}
      />
      {/* 当日多条班次：先列出该日班次供选择，再进入编辑 */}
      <Modal
        title={pickDayList ? `${pickDayList.day.format('YYYY-MM-DD')} 班次（${pickDayList.shifts.length} 条）` : ''}
        open={!!pickDayList}
        footer={null}
        onCancel={() => setPickDayList(null)}
        destroyOnClose
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {pickDayList?.shifts.map((s) => (
            <div
              key={s.id}
              role="button"
              tabIndex={0}
              onClick={() => {
                setPickDayList(null)
                onPickDay(pickDayList.day, [s])
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') { setPickDayList(null); onPickDay(pickDayList.day, [s]) } }}
              style={{
                border: '1px solid #d9d9d9',
                borderRadius: 8,
                padding: '10px 12px',
                cursor: 'pointer',
                minHeight: 44,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span>
                <b>{usernameOf(s.adminUserId)}</b>
                <span style={{ color: '#888', marginLeft: 10, fontSize: 12 }}>
                  {dayjs(s.startAt).format('HH:mm')} ~ {dayjs(s.endAt).format('HH:mm')}
                </span>
              </span>
              <span style={{ color: '#1A873B', fontSize: 12 }}>编辑 ›</span>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  )
}

export default DutyShiftCalendar
